import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import {
  AlertVariant,
  Button,
  Form,
  FormGroup,
  Grid,
  GridItem,
  Modal,
  ModalVariant,
  Popover,
  TextArea,
  TextInput,
} from '@patternfly/react-core';
import HelpIcon from '@patternfly/react-icons/dist/esm/icons/help-icon';
import { AlertGroupProvider } from '@ole-ui/ole-ui-components';
import GenericViewError from 'components/Error/GenericViewError';
import useMobxStores from 'hooks/useMobxStores';
import { handleError } from 'services/ErrorService';
import './styles.scss';

type OrganizationFormSnapshot = {
  name: string;
  orderNumbers: string;
  adminUsers: string;
};

type OrganizationFormProps = {
  onClose: () => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  onRegisterCloseRequest?: (handler: (() => void) | null) => void;
};

const OrganizationForm = ({
  onClose,
  onSubmittingChange,
  onRegisterCloseRequest,
}: OrganizationFormProps) => {
  const { t } = useTranslation();
  const { addAlert } = AlertGroupProvider.useAlertGroupContext();
  const { organizationStore } = useMobxStores();
  const { editingOrganization, newOrgPayload } = organizationStore;
  const isEditing = Boolean(editingOrganization.id);
  const companyNameRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
  const [baseline] = useState<OrganizationFormSnapshot>(() => ({
    name: editingOrganization.name,
    orderNumbers: editingOrganization.orderNumbers,
    adminUsers: editingOrganization.adminUsers,
  }));

  const isDirty = useMemo(
    () =>
      editingOrganization.name !== baseline.name ||
      editingOrganization.orderNumbers !== baseline.orderNumbers ||
      editingOrganization.adminUsers !== baseline.adminUsers,
    [
      editingOrganization.name,
      editingOrganization.orderNumbers,
      editingOrganization.adminUsers,
      baseline,
    ],
  );

  useEffect(() => {
    const input = companyNameRef.current;
    if (!input) {
      return;
    }

    // Defer so PatternFly Modal focus management finishes first.
    const frame = requestAnimationFrame(() => {
      input.focus();
      input.select();
    });

    // eslint-disable-next-line consistent-return
    return () => cancelAnimationFrame(frame);
  }, []);

  const setSubmitting = (next: boolean) => {
    setIsSubmitting(next);
    onSubmittingChange?.(next);
  };

  const requestClose = () => {
    if (isSubmitting) {
      return;
    }

    if (isDirty) {
      setIsDiscardConfirmOpen(true);
      return;
    }

    onClose();
  };

  useEffect(() => {
    onRegisterCloseRequest?.(requestClose);
    return () => {
      onRegisterCloseRequest?.(null);
    };
  });

  const handleDiscardConfirm = () => {
    setIsDiscardConfirmOpen(false);
    onClose();
  };

  const handleSubmit = async (
    event?: React.FormEvent<HTMLFormElement> | React.MouseEvent,
  ) => {
    event?.preventDefault();

    if (isSubmitting) {
      return;
    }

    const editingId = editingOrganization.id;
    const payload = { ...newOrgPayload };
    const companyName = payload.name;

    setSubmitting(true);

    try {
      if (editingId) {
        await organizationStore.updateOrganization(editingId, payload);
        addAlert({
          key: `organization-update-success-${Date.now()}`,
          title: t('Company updated successfully'),
          children: t('{{name}} has been updated.', { name: companyName }),
          variant: AlertVariant.success,
          timeout: 5000,
        });
      } else {
        await organizationStore.addOrganization(payload);
        addAlert({
          key: `organization-create-success-${Date.now()}`,
          title: t('Company created successfully'),
          children: t('{{name}} has been created.', { name: companyName }),
          variant: AlertVariant.success,
          timeout: 5000,
        });
      }

      setSubmitting(false);
      onClose();
    } catch {
      if (editingId) {
        addAlert({
          key: `organization-update-error-${Date.now()}`,
          title: t('Could not update company'),
          children: t(
            'Something went wrong while updating {{name}}. Please try again.',
            { name: companyName },
          ),
          variant: AlertVariant.danger,
          timeout: 8000,
        });
      } else {
        addAlert({
          key: `organization-create-error-${Date.now()}`,
          title: t('Could not create company'),
          children: t(
            'Something went wrong while creating {{name}}. Please try again.',
            { name: companyName },
          ),
          variant: AlertVariant.danger,
          timeout: 8000,
        });
      }
      setSubmitting(false);
    }
  };

  const isSubmitDisabled =
    isSubmitting ||
    !editingOrganization.name.trim().length ||
    !newOrgPayload.adminUsers.length;

  return (
    <>
      <Form className="organization-form" onSubmit={handleSubmit}>
        <FormGroup
          label={t('Company name')}
          isRequired
          fieldId="organization-company-name"
        >
          <TextInput
            ref={companyNameRef}
            id="organization-company-name"
            type="text"
            maxLength={120}
            autoCapitalize="off"
            autoCorrect="off"
            placeholder={t('e.g. Contoso')}
            value={editingOrganization.name}
            isDisabled={isSubmitting}
            onFocus={(event) => event.currentTarget.select()}
            onChange={(_event, value) => {
              editingOrganization.name = value;
            }}
          />
        </FormGroup>
        <Grid hasGutter>
          <GridItem md={6}>
            <FormGroup
              label={t('Order numbers')}
              fieldId="organization-order-numbers"
              labelIcon={
                <Popover
                  aria-label={t('Order numbers help')}
                  bodyContent={t(
                    'Enter order numbers separated by commas or line breaks.',
                  )}
                >
                  <Button
                    variant="plain"
                    aria-label={t('More information for order numbers')}
                    icon={<HelpIcon />}
                    isDisabled={isSubmitting}
                  />
                </Popover>
              }
            >
              <TextArea
                id="organization-order-numbers"
                rows={5}
                autoCapitalize="off"
                autoCorrect="off"
                resizeOrientation="vertical"
                placeholder={t('e.g. 12345678, 87654321')}
                value={editingOrganization.orderNumbers}
                isDisabled={isSubmitting}
                onChange={(_event, value) => {
                  editingOrganization.orderNumbers = value;
                }}
              />
            </FormGroup>
          </GridItem>
          <GridItem md={6}>
            <FormGroup
              label={t('Admins')}
              isRequired
              fieldId="organization-admins"
              labelIcon={
                <Popover
                  aria-label={t('Admins help')}
                  bodyContent={t(
                    'Enter admin usernames separated by commas or line breaks.',
                  )}
                >
                  <Button
                    variant="plain"
                    aria-label={t('More information for admins')}
                    icon={<HelpIcon />}
                    isDisabled={isSubmitting}
                  />
                </Popover>
              }
            >
              <TextArea
                id="organization-admins"
                rows={5}
                autoCapitalize="off"
                autoCorrect="off"
                resizeOrientation="vertical"
                placeholder={t('e.g. jsmith, ajones')}
                value={editingOrganization.adminUsers}
                isDisabled={isSubmitting}
                onChange={(_event, value) => {
                  editingOrganization.adminUsers = value;
                }}
              />
            </FormGroup>
          </GridItem>
        </Grid>
        <div className="organization-form__actions">
          <Button
            variant="link"
            onClick={requestClose}
            isDisabled={isSubmitting}
          >
            {t('Cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            isDisabled={isSubmitDisabled}
            isLoading={isSubmitting}
            spinnerAriaValueText={t('Loading')}
            onClick={handleSubmit}
          >
            {isEditing ? t('Update') : t('Create')}
          </Button>
        </div>
      </Form>
      <Modal
        variant={ModalVariant.small}
        title={t('Discard changes?')}
        isOpen={isDiscardConfirmOpen}
        onClose={() => setIsDiscardConfirmOpen(false)}
        actions={[
          <Button key="discard" variant="danger" onClick={handleDiscardConfirm}>
            {t('Discard')}
          </Button>,
          <Button
            key="keep"
            variant="link"
            onClick={() => setIsDiscardConfirmOpen(false)}
          >
            {t('Keep editing')}
          </Button>,
        ]}
      >
        {t(
          'You have unsaved changes. If you leave now, your changes will be lost.',
        )}
      </Modal>
    </>
  );
};

export default withErrorBoundary(
  observer(OrganizationForm),
  GenericViewError,
  handleError,
);
